import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {WebOnboardingService} from '../../_services/web-onboarding.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

export enum OnboardingStep {
  Welcome = 0,
  DniFront = 1,
  DniBack = 2,
  DniConfirmation = 3,
  Liveness = 4,
  Phone = 5,
  PhoneVerification = 6,
  Email = 7,
  EmailVerification = 8,
  Location = 9,
  Services = 10,
  References = 11,
  Password = 12,
  Result = 13,
}

@Component({
  selector: 'app-onboarding-container',
  templateUrl: './onboarding-container.component.html',
  styleUrls: ['./onboarding-container.component.css'],
})
export class OnboardingContainerComponent implements OnInit, OnDestroy {
  OnboardingStep = OnboardingStep;
  currentStep: OnboardingStep = OnboardingStep.Welcome;

  token: string = '';
  sessionValid: boolean = false;
  loading: boolean = true;
  errorMessage: string = '';

  // Data passed between steps
  invitationEmail: string | null = null;
  identityData: any = null;
  validationToken: string = '';
  prefillPhone: string | null = null;
  prefillEmail_: string | null = null;
  requestAdditionalInformation: boolean = false;
  isPasswordRequired: boolean = false;
  success: boolean = false;

  // DNI photos
  dniFrontFile: File | null = null;

  // Verification IDs
  smsVerificationId: string = '';
  emailVerificationId: string = '';

  // References data (passed to password step)
  savedReferences: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private onboardingService: WebOnboardingService,
  ) {}

  ngOnInit(): void {
    // Check if we're resuming after FLD (coming back from SANII)
    const biometryId = this.route.snapshot.queryParamMap.get('biometryId');
    if (biometryId) {
      this.resumeAfterFld(biometryId);
      return;
    }

    this.token = this.route.snapshot.paramMap.get('token') || '';

    if (!this.token) {
      this.errorMessage = 'Link inválido.';
      this.loading = false;
      return;
    }

    this.onboardingService
      .validateToken(this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.sessionValid = true;
          this.invitationEmail = response.email || null;
          this.loading = false;
        },
        error: (err) => {
          this.errorMessage = err.error?.error || 'El link es inválido o ha expirado.';
          this.loading = false;
        },
      });
  }

  private fldRetryCount = 0;
  private readonly FLD_MAX_RETRIES = 10;
  private readonly FLD_INITIAL_DELAY = 5000;
  private readonly FLD_RETRY_DELAY = 3000;

  private resumeAfterFld(biometryId: string): void {
    this.sessionValid = true;
    this.loading = true;

    // Restore state saved before SANII redirect
    const savedState = sessionStorage.getItem('onboarding_state');
    if (savedState) {
      const state = JSON.parse(savedState);
      this.validationToken = state.validationToken || '';
      this.identityData = state.identityData || null;
      this.invitationEmail = state.invitationEmail || null;
      this.prefillPhone = state.prefillPhone || null;
      this.prefillEmail_ = state.prefillEmail_ || null;
      this.requestAdditionalInformation = state.requestAdditionalInformation || false;
      this.isPasswordRequired = state.isPasswordRequired || false;
      sessionStorage.removeItem('onboarding_state');
    }

    // Wait before first call to give SANII webhook time to arrive
    const delay = this.fldRetryCount === 0 ? this.FLD_INITIAL_DELAY : this.FLD_RETRY_DELAY;

    setTimeout(() => {
      this.onboardingService
        .verifyFaceLiveness()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response?.code === 'FACE_LIVENESS_DETECTION_COMPLETED' ||
                (response?.payload && !response.errors)) {
              this.loading = false;
              this.fldRetryCount = 0;
              this.goToStep(OnboardingStep.Phone);
            } else if (this.fldRetryCount < this.FLD_MAX_RETRIES) {
              this.fldRetryCount++;
              this.resumeAfterFld(biometryId);
            } else {
              this.loading = false;
              this.fldRetryCount = 0;
              this.errorMessage = 'La verificación facial no se completó. Intentá nuevamente.';
            }
          },
          error: (err) => {
            // 422 = biometry INVALID, 403 = blocked → definitive failures
            if (err.status === 422 || err.status === 403) {
              this.loading = false;
              this.fldRetryCount = 0;
              const body = err.error;
              if (body?.errors?.length) {
                this.errorMessage = body.errors.map((e: any) => e.message).filter(Boolean).join('\n');
              } else if (body?.message) {
                this.errorMessage = body.message;
              } else {
                this.errorMessage = 'La verificación facial falló.';
              }
            } else if (this.fldRetryCount < this.FLD_MAX_RETRIES) {
              // Any other error (202, 500, 502, timeout) → retry
              this.fldRetryCount++;
              this.resumeAfterFld(biometryId);
            } else {
              this.loading = false;
              this.fldRetryCount = 0;
              this.errorMessage = 'Error en la verificación facial. Intentá nuevamente.';
            }
          },
        });
    }, delay);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToStep(step: OnboardingStep): void {
    this.currentStep = step;
    window.scrollTo(0, 0);
  }

  onWelcomeAccepted(): void {
    this.goToStep(OnboardingStep.DniFront);
  }

  onDniFrontCaptured(file: File): void {
    this.dniFrontFile = file;
    this.goToStep(OnboardingStep.DniBack);
  }

  onDniValidationFailed(): void {
    this.dniFrontFile = null;
    this.goToStep(OnboardingStep.DniFront);
  }

  onDniValidated(data: any): void {
    this.identityData = data.identity;
    this.validationToken = data.token || '';
    this.prefillPhone = data.mobilePhone || null;
    this.prefillEmail_ = data.email || this.invitationEmail;
    this.requestAdditionalInformation = data.requestAdditionalInformation || false;
    this.isPasswordRequired = data.isPasswordRequired || false;
    this.goToStep(OnboardingStep.DniConfirmation);
  }

  onDniConfirmed(): void {
    // Save state before liveness redirect (SANII will navigate away from the app)
    sessionStorage.setItem('onboarding_state', JSON.stringify({
      validationToken: this.validationToken,
      identityData: this.identityData,
      invitationEmail: this.invitationEmail,
      prefillPhone: this.prefillPhone,
      prefillEmail_: this.prefillEmail_,
      requestAdditionalInformation: this.requestAdditionalInformation,
      isPasswordRequired: this.isPasswordRequired,
    }));
    this.goToStep(OnboardingStep.Liveness);
  }

  onLivenessCompleted(): void {
    this.goToStep(OnboardingStep.Phone);
  }

  onPhoneSubmitted(data: {verificationId: string}): void {
    this.smsVerificationId = data.verificationId;
    this.goToStep(OnboardingStep.PhoneVerification);
  }

  onPhoneVerified(): void {
    this.goToStep(OnboardingStep.Email);
  }

  onEmailSubmitted(data: {verificationId: string}): void {
    this.emailVerificationId = data.verificationId;
    this.goToStep(OnboardingStep.EmailVerification);
  }

  onEmailVerified(data: any): void {
    if (this.requestAdditionalInformation) {
      this.goToStep(OnboardingStep.Location);
    } else {
      this.goToStep(OnboardingStep.References);
    }
  }

  onLocationSaved(): void {
    this.goToStep(OnboardingStep.Services);
  }

  onServicesSaved(): void {
    this.goToStep(OnboardingStep.References);
  }

  onReferencesSaved(data: any): void {
    this.savedReferences = data?._references || [];
    if (this.isPasswordRequired) {
      this.goToStep(OnboardingStep.Password);
    } else {
      this.success = true;
      this.goToStep(OnboardingStep.Result);
    }
  }

  onPasswordSaved(): void {
    this.success = true;
    this.goToStep(OnboardingStep.Result);
  }

  onCancel(): void {
    if (confirm('¿Desea cancelar el proceso de registración?')) {
      this.router.navigate(['/login']);
    }
  }
}
